import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  saveRecording,
  getRecording,
  getRetentionDays,
  setRetentionDays,
} from "@/lib/recordings-db";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { getLocalizedName } from "@/lib/utils";
import { resolveLocalized, type LocalizedText } from "@arna-med/shared";
import {
  Mic,
  MicOff,
  CheckCircle,
  FileText,
  Upload,
  RefreshCw,
  Pencil,
  MessageSquare,
  Save,
  AlertCircle,
  Loader2,
  Pause,
  Play,
} from "lucide-react";
import { useConsultation, useCompleteConsultation } from "@/hooks/api/useConsultations";
import { useTranscriptions, useSaveTranscription, useReplaceTranscription, useTranscribeAudio, useLogRecordingDuration } from "@/hooks/api/useTranscriptions";
import { useReport, useGenerateReport, usePatchReport } from "@/hooks/api/useReports";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";
import { ReportPanel } from "@/components/ReportPanel";
import { AudioWaveform } from "@/components/AudioWaveform";

const VITALS_NORMAL_VALUE =
  "T: 36.6 °C. АД: 120/80 мм.рт.ст. ЧСС: 72 уд/мин. ЧДД: 16 /мин. SpO2: 98%.";

interface VitalsField {
  key: string;
  isGeneral: boolean;
}

function findMissingVitalsField(reportData: unknown): VitalsField | null {
  if (!reportData || typeof reportData !== "object") return null;
  const r = reportData as Record<string, unknown>;
  const structured = r.structuredData as Record<string, unknown> | null | undefined;
  const general = r.generalData as Record<string, unknown> | null | undefined;

  for (const [isGeneral, source] of [[false, structured], [true, general]] as [boolean, Record<string, unknown> | null | undefined][]) {
    for (const [key, value] of Object.entries(source ?? {})) {
      if (typeof value === "string") {
        const matches = value.match(/не измерялось/gi);
        if ((matches?.length ?? 0) >= 3) {
          return { key, isGeneral };
        }
      }
    }
  }
  return null;
}

type Tab = "transcript" | "report";
type DialogTurn = { speaker: "Doctor" | "Patient"; text: string };

function parseDialog(text: string): DialogTurn[] | null {
  const lines = text.split("\n");
  const turns: DialogTurn[] = [];

  for (const line of lines) {
    const doctorMatch = line.match(/^Doctor\s*:\s*(.*)/i);
    const patientMatch = line.match(/^Patient\s*:\s*(.*)/i);
    if (doctorMatch) {
      turns.push({ speaker: "Doctor", text: doctorMatch[1].trim() });
    } else if (patientMatch) {
      turns.push({ speaker: "Patient", text: patientMatch[1].trim() });
    } else if (turns.length > 0 && line.trim()) {
      turns[turns.length - 1].text += "\n" + line.trim();
    }
  }

  return turns.length > 0 ? turns : null;
}

export default function Consultation() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<Tab>("transcript");
  const [editableText, setEditableText] = useState("");
  const [interimText, setInterimText] = useState("");
  const [showCompletionOverlay, setShowCompletionOverlay] = useState(false);
  const [isEditingDialog, setIsEditingDialog] = useState(false);
  const [missingBlockingFields, setMissingBlockingFields] = useState<LocalizedText[] | null>(null);
  const [showNoReportDialog, setShowNoReportDialog] = useState(false);
  const [showVitalsDialog, setShowVitalsDialog] = useState(false);
  const [vitalsField, setVitalsField] = useState<VitalsField | null>(null);
  const dialogTurns = useMemo(() => parseDialog(editableText), [editableText]);
  const [localRecordingUrl, setLocalRecordingUrl] = useState<string | null>(null);
  const [recordingSavedAt, setRecordingSavedAt] = useState<number | null>(null);
  const [retentionDays, setRetentionDaysState] = useState(() => getRetentionDays());
  const initializedRef = useRef(false);
  const tabInitializedRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const localRecordingUrlRef = useRef<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const completionNavTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: consultation } = useConsultation(id);
  const { data: transcriptions } = useTranscriptions(id);
  const { data: reportData, isFetched: isReportFetched } = useReport(id);
  const reportExists = reportData != null;

  const saveMutation = useSaveTranscription(id);
  const replaceTranscriptionMutation = useReplaceTranscription(id);
  const completeMutation = useCompleteConsultation(id);
  const generateMutation = useGenerateReport(id);
  const patchReportMutation = usePatchReport(id);
  const transcribeAudioMutation = useTranscribeAudio(id);
  const logDurationMutation = useLogRecordingDuration(id);

  useEffect(() => {
    if (reportExists && !tabInitializedRef.current) {
      tabInitializedRef.current = true;
      setActiveTab("report");
    }
  }, [reportExists]);

  useEffect(() => {
    if (transcriptions && !initializedRef.current) {
      initializedRef.current = true;
      setEditableText(transcriptions.map((t) => t.text).join("\n"));
    }
  }, [transcriptions]);

  useEffect(() => {
    if (!id) return;
    getRecording(id).then((rec) => {
      if (rec) {
        const url = URL.createObjectURL(rec.blob);
        localRecordingUrlRef.current = url;
        setLocalRecordingUrl(url);
        setRecordingSavedAt(rec.savedAt);
      }
    });
    return () => {
      if (localRecordingUrlRef.current) {
        URL.revokeObjectURL(localRecordingUrlRef.current);
      }
    };
  }, [id]);

  useEffect(() => {
    return () => {
      if (completionNavTimerRef.current) clearTimeout(completionNavTimerRef.current);
    };
  }, []);

  const doComplete = useCallback((force?: boolean) => {
    completeMutation.mutate(force, {
      onSuccess: () => {
        setShowCompletionOverlay(true);
        const patientId = consultation?.patientId;
        if (patientId) {
          completionNavTimerRef.current = setTimeout(() => {
            navigate(`/patients/${patientId}`);
          }, 2000);
        }
      },
      onError: (err: unknown) => {
        const data = (err as { response?: { data?: { code?: string; missingFields?: LocalizedText[] } } })?.response?.data;
        if (data?.code === "MISSING_BLOCKING_FIELDS" && data.missingFields) {
          setMissingBlockingFields(data.missingFields);
        }
      },
    });
  }, [completeMutation, consultation?.patientId, navigate]);

  const completeWithTranscript = useCallback(() => {
    const trimmed = editableText.trim();
    if (trimmed) {
      replaceTranscriptionMutation.mutate(trimmed, {
        onSettled: () => doComplete(),
      });
    } else {
      doComplete();
    }
  }, [editableText, replaceTranscriptionMutation, doComplete]);

  const handleComplete = useCallback(() => {
    // Completing hides every action on this page, so make sure the doctor
    // is not closing a session that has a transcript but no report yet.
    if (!reportExists && editableText.trim()) {
      setShowNoReportDialog(true);
      return;
    }
    completeWithTranscript();
  }, [reportExists, editableText, completeWithTranscript]);

  const handleGenerate = useCallback(() => {
    generateMutation.mutate(editableText, {
      onSuccess: (data) => {
        tabInitializedRef.current = true;
        setActiveTab("report");
        const reportPayload = (data as { data?: unknown })?.data;
        const vf = findMissingVitalsField(reportPayload);
        if (vf) {
          setVitalsField(vf);
          setShowVitalsDialog(true);
        }
      },
      onError: () => toast.error(t("consultation.reportGenerationError")),
    });
  }, [generateMutation, editableText, t]);

  const handleRecordingComplete = useCallback(
    (blob: Blob, mimeType: string, durationSeconds: number) => {
      if (!id) return;
      if (durationSeconds < 10) {
        toast.warning(t("consultation.recordingTooShort"));
        return;
      }
      const now = Date.now();
      void saveRecording(id, blob, mimeType).then(() => {
        if (localRecordingUrlRef.current) {
          URL.revokeObjectURL(localRecordingUrlRef.current);
        }
        const url = URL.createObjectURL(blob);
        localRecordingUrlRef.current = url;
        setLocalRecordingUrl(url);
        setRecordingSavedAt(now);
      });
      logDurationMutation.mutate(durationSeconds);
    },
    [id, logDurationMutation],
  );

  const handleTranscript = useCallback(
    (text: string, isFinal: boolean, clientRequestId?: string) => {
      if (isFinal) {
        setInterimText("");
        setEditableText((prev) => (prev ? prev + "\n" + text : text));
        saveMutation.mutate({ text, clientRequestId });
      } else {
        setInterimText(text);
      }
    },
    [saveMutation],
  );

  const {
    isRecording,
    isPaused,
    isProcessing,
    pendingChunks,
    transcriptionError,
    retryingChunks,
    failedChunks,
    retryFailedChunks,
    isSupported,
    start,
    stop,
    pause,
    resume,
    streamRef,
  } = useSpeechRecognition({
    onTranscript: handleTranscript,
    onRecordingComplete: handleRecordingComplete,
  });

  const handleRetryTranscription = useCallback(async () => {
    if (!localRecordingUrl) return;
    try {
      const res = await fetch(localRecordingUrl);
      const blob = await res.blob();
      const file = new File([blob], "recording.webm", { type: blob.type || "audio/webm" });
      transcribeAudioMutation.mutate(
        { file, durationSeconds: 0 },
        { onSuccess: (data) => setEditableText(data.text) },
      );
    } catch {
      // blob URL expired or unavailable
    }
  }, [localRecordingUrl, transcribeAudioMutation]);

  const handleTranscribeLocalRecording = useCallback(() => {
    if (editableText.trim() && !window.confirm(t("consultation.transcribeAgainConfirm"))) return;
    void handleRetryTranscription();
  }, [editableText, handleRetryTranscription, t]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "0";
    const maxH = 800;
    el.style.height = Math.min(el.scrollHeight, maxH) + "px";
    el.style.overflowY = el.scrollHeight > maxH ? "auto" : "hidden";
  }, [editableText, interimText]);

  const handleStart = useCallback(() => {
    if (reportExists) {
      if (!window.confirm(t("consultation.restartConfirm"))) return;
    }
    setInterimText("");
    start();
  }, [start, reportExists, t]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      const audio = new Audio(url);
      audio.addEventListener("loadedmetadata", () => {
        const durationSeconds = isFinite(audio.duration) ? audio.duration : 0;
        URL.revokeObjectURL(url);
        transcribeAudioMutation.mutate(
          { file, durationSeconds },
          { onSuccess: (data) => setEditableText(data.text) },
        );
      });
      audio.addEventListener("error", () => {
        URL.revokeObjectURL(url);
        transcribeAudioMutation.mutate(
          { file, durationSeconds: 0 },
          { onSuccess: (data) => setEditableText(data.text) },
        );
      });
      e.target.value = "";
    }
  };

  const isCompleted = consultation?.status === "completed";
  const isProcessingAudio = transcribeAudioMutation.isPending;
  const isGenerating = generateMutation.isPending;
  // A session closed before a report was generated would otherwise be a dead end.
  const canGenerateAfterCompletion = isCompleted && isReportFetched && !reportExists;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="flex flex-col gap-2">
          <div>
            <h1 className="text-xl font-bold">{consultation?.patientName}</h1>
            <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-sm text-muted-foreground">
              {consultation?.doctorName && (
                <span><span className="font-medium text-foreground">{t("consultation.dialogDoctor")}:</span> {consultation.doctorName}</span>
              )}
              {consultation?.doctorSpeciality && (
                <span><span className="font-medium text-foreground">{t("consultation.specialty")}:</span> {getLocalizedName(consultation.doctorSpeciality, i18n.language)}</span>
              )}
              {consultation?.templateName && (
                <span><span className="font-medium text-foreground">{t("consultation.template")}:</span> {getLocalizedName(consultation.template ?? { name: consultation.templateName }, i18n.language)}</span>
              )}
            </div>
          </div>
          {!isCompleted && (
            <div className="flex gap-2 flex-wrap">
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                className="sr-only"
                onChange={handleFileChange}
              />
              {isSupported && (
                <>
                  <Button
                    variant={isRecording ? "destructive" : "default"}
                    className={
                      !isRecording ? "bg-red-400 hover:bg-red-500 text-white" : ""
                    }
                    onClick={isRecording ? stop : handleStart}
                    disabled={isProcessingAudio || (!isRecording && isProcessing)}
                  >
                    {isRecording ? (
                      <>
                        <MicOff className="h-4 w-4 mr-2" />
                        {t("consultation.stopRecording")}
                      </>
                    ) : (
                      <>
                        <Mic className="h-4 w-4 mr-2" />
                        {t("consultation.startRecording")}
                      </>
                    )}
                  </Button>
                  {isRecording && (
                    <Button
                      variant="outline"
                      onClick={isPaused ? resume : pause}
                    >
                      {isPaused ? (
                        <>
                          <Play className="h-4 w-4 mr-2" />
                          {t("consultation.resumeRecording")}
                        </>
                      ) : (
                        <>
                          <Pause className="h-4 w-4 mr-2" />
                          {t("consultation.pauseRecording")}
                        </>
                      )}
                    </Button>
                  )}
                </>
              )}
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={isProcessingAudio || isRecording}
              >
                {isProcessingAudio ? (
                  <>
                    <div className="h-4 w-4 mr-2 rounded-full border-2 border-current border-t-transparent animate-spin" />
                    {t("consultation.transcribing")}
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    {t("consultation.uploadAudio")}
                  </>
                )}
              </Button>

              {/* Sits next to "Pause": a doctor reaching for pause and hitting
                  this closed the session mid-visit. Also held while the last
                  chunks are still being transcribed, so none are lost. */}
              <Button
                variant="outline"
                onClick={handleComplete}
                disabled={
                  completeMutation.isPending ||
                  replaceTranscriptionMutation.isPending ||
                  isRecording ||
                  isProcessing
                }
                title={isRecording ? t("consultation.completeSessionWhileRecording") : undefined}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {t("consultation.completeSession")}
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={isGenerating || isProcessingAudio || !editableText.trim()}
              >
                {isGenerating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4 mr-2" />
                )}
                {isGenerating ? t("consultation.generatingReport") : t("consultation.generateReport")}
              </Button>
            </div>
          )}
          {canGenerateAfterCompletion && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span className="flex-1 min-w-48">{t("consultation.completedWithoutReport")}</span>
              <Button
                onClick={handleGenerate}
                disabled={isGenerating || !editableText.trim()}
              >
                {isGenerating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4 mr-2" />
                )}
                {isGenerating ? t("consultation.generatingReport") : t("consultation.generateReport")}
              </Button>
            </div>
          )}
        </div>

        {reportExists && (
          <div className="flex border-b">
            {(["transcript", "report"] as Tab[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === tab
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {tab === "transcript"
                  ? t("consultation.transcript")
                  : t("report.title")}
              </button>
            ))}
          </div>
        )}

        {activeTab === "transcript" && (
          <>
            {isRecording && streamRef.current && (
              <AudioWaveform stream={streamRef.current} />
            )}

            {isRecording && (
              <div className={`flex items-center gap-2 text-sm ${isPaused ? "text-yellow-600" : "text-red-600 animate-pulse"}`}>
                {isPaused ? <Pause className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                {isPaused ? t("consultation.pauseRecording") : t("consultation.recording")}
              </div>
            )}
            {(isProcessing || isProcessingAudio) && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground animate-pulse">
                <div className="h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
                {retryingChunks > 0
                  ? t("consultation.reconnecting")
                  : t("consultation.transcribing")}
                {pendingChunks > 0 && (
                  <span className="text-xs tabular-nums">
                    ({pendingChunks})
                  </span>
                )}
              </div>
            )}

            {failedChunks > 0 && (
              <div className="flex flex-wrap items-center gap-3 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>
                  {t("consultation.chunksPendingUpload", { count: failedChunks })}
                </span>
                <Button size="sm" variant="outline" onClick={retryFailedChunks}>
                  {t("consultation.retryUpload")}
                </Button>
              </div>
            )}

            {transcriptionError && failedChunks === 0 && !editableText.trim() && !isProcessing && !isProcessingAudio && !isRecording && (
              <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{t("consultation.transcriptionFailed")}</span>
              </div>
            )}

            {localRecordingUrl && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    {t("consultation.localRecording")}
                    {recordingSavedAt && (
                      <span className="text-xs font-normal text-muted-foreground">
                        {t("consultation.localRecordingExpiry", {
                          date: new Date(
                            recordingSavedAt +
                              retentionDays * 24 * 60 * 60 * 1000,
                          ).toLocaleDateString(),
                        })}
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <audio controls src={localRecordingUrl} className="w-full" />
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{t("consultation.retentionDays")}</span>
                    <input
                      type="number"
                      min={1}
                      max={365}
                      value={retentionDays}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val > 0) {
                          setRetentionDays(val);
                          setRetentionDaysState(val);
                        }
                      }}
                      className="w-16 border rounded px-1 py-0.5 text-xs bg-background"
                    />
                    <span>{t("consultation.days")}</span>
                  </div>
                  {!isCompleted && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={handleTranscribeLocalRecording}
                      disabled={isProcessingAudio || isRecording}
                    >
                      {isProcessingAudio ? (
                        <>
                          <div className="h-3.5 w-3.5 mr-1.5 rounded-full border-2 border-current border-t-transparent animate-spin" />
                          {t("consultation.transcribing")}
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                          {editableText.trim()
                            ? t("consultation.retryTranscription")
                            : t("consultation.transcribeRecording")}
                        </>
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {isGenerating && (
              <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
                <div className="h-full w-2/5 bg-primary rounded-full animate-progress" />
              </div>
            )}

            {editableText && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {t("consultation.transcript")}
                      {transcriptions?.some((t) => t.isEdited) && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 font-medium">
                          {t("consultation.transcriptEdited")}
                        </span>
                      )}
                    </div>
                    {!isCompleted && dialogTurns && (
                      <button
                        onClick={() => setIsEditingDialog((v) => !v)}
                        className="flex items-center gap-1 text-xs font-normal text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {isEditingDialog ? (
                          <>
                            <MessageSquare className="h-3.5 w-3.5" />
                            {t("consultation.viewDialog")}
                          </>
                        ) : (
                          <>
                            <Pencil className="h-3.5 w-3.5" />
                            {t("consultation.editRawText")}
                          </>
                        )}
                      </button>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dialogTurns && !isEditingDialog ? (
                    <div className="space-y-3">
                      {dialogTurns.map((turn, i) => (
                        <div
                          key={i}
                          className={`flex gap-2 ${turn.speaker === "Doctor" ? "flex-row-reverse" : "flex-row"}`}
                        >
                          <div
                            className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${turn.speaker === "Doctor" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                          >
                            {turn.speaker === "Doctor"
                              ? t("consultation.dialogDoctor")[0]
                              : t("consultation.dialogPatient")[0]}
                          </div>
                          <div
                            className={`rounded-lg px-3 py-2 text-sm max-w-[78%] ${turn.speaker === "Doctor" ? "bg-primary/10" : "bg-muted"}`}
                          >
                            <p className="text-xs font-medium text-muted-foreground mb-0.5">
                              {turn.speaker === "Doctor"
                                ? t("consultation.dialogDoctor")
                                : t("consultation.dialogPatient")}
                            </p>
                            <p className="whitespace-pre-wrap leading-relaxed">
                              {turn.text}
                            </p>
                          </div>
                        </div>
                      ))}
                      {interimText && (
                        <p className="text-sm text-muted-foreground italic pl-9">
                          {interimText}
                        </p>
                      )}
                    </div>
                  ) : (
                    <>
                      <textarea
                        ref={textareaRef}
                        className="w-full min-h-48 whitespace-pre-wrap text-sm leading-relaxed bg-transparent resize-y outline-none"
                        value={
                          editableText +
                          (interimText
                            ? (editableText ? "\n" : "") + interimText
                            : "")
                        }
                        onChange={(e) => setEditableText(e.target.value)}
                        placeholder={
                          isSupported
                            ? t("consultation.startRecording") + "..."
                            : "Web Speech API not supported in this browser."
                        }
                        readOnly={isCompleted}
                      />
                      {!isCompleted && editableText.trim() && (
                        <div className="flex justify-end pt-2">
                          <Button
                            onClick={() => replaceTranscriptionMutation.mutate(editableText.trim())}
                            disabled={replaceTranscriptionMutation.isPending}
                          >
                            <Save className="h-4 w-4 mr-2" />
                            {replaceTranscriptionMutation.isPending ? t("common.saving") : t("common.save")}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                  {(isRecording || isProcessing) && (
                    <div className="flex items-center gap-1 pt-2 pl-1">
                      {[0, 1, 2].map((i) => (
                        <span
                          key={i}
                          className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce"
                          style={{ animationDelay: `${i * 150}ms` }}
                        />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}

        {activeTab === "report" && reportExists && (
          <>
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={handleGenerate}
                disabled={isGenerating || isProcessingAudio || !editableText.trim()}
              >
                {isGenerating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {isGenerating ? t("consultation.generatingReport") : t("consultation.regenerateReport")}
              </Button>
            </div>
            <ReportPanel id={id!} isRegenerating={isGenerating} />
          </>
        )}
      </div>

      {/* Session completed — animated success overlay */}
      {showCompletionOverlay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm animate-session-fade-in">
          <div className="flex flex-col items-center gap-6 p-8 max-w-sm w-full text-center animate-session-scale-in">
            {/* Checkmark with ripple rings */}
            <div className="relative flex items-center justify-center">
              <div
                className="absolute w-24 h-24 rounded-full border-2 border-green-400 animate-session-ripple"
                style={{ animationDelay: "0.2s" }}
              />
              <div
                className="absolute w-24 h-24 rounded-full border-2 border-green-300 animate-session-ripple"
                style={{ animationDelay: "0.5s" }}
              />
              <div className="w-24 h-24 rounded-full bg-green-50 dark:bg-green-950 flex items-center justify-center">
                <CheckCircle className="w-14 h-14 text-green-500 animate-session-check" />
              </div>
            </div>

            {/* Title */}
            <div className="animate-session-slide-up" style={{ animationDelay: "0.2s" }}>
              <h2 className="text-2xl font-bold text-foreground">
                {t("consultation.sessionCompleted")}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {consultation?.patientName}
              </p>
            </div>

            {/* Timeline steps */}
            <div className="w-full space-y-3 text-left">
              {(
                [
                  "consultation.completionStepRecorded",
                  "consultation.completionStepTranscribed",
                  "consultation.completionStepHistory",
                ] as const
              ).map((key, i) => (
                <div
                  key={key}
                  className="flex items-center gap-3 animate-session-slide-up"
                  style={{ animationDelay: `${0.35 + i * 0.15}s` }}
                >
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                    <CheckCircle className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="text-sm text-foreground">{t(key)}</span>
                </div>
              ))}
            </div>

            {/* Action buttons */}
            <div
              className="animate-session-slide-up flex flex-col gap-2 w-full"
              style={{ animationDelay: "0.85s" }}
            >
              {consultation?.patientId && (
                <Button
                  onClick={() => {
                    if (completionNavTimerRef.current) clearTimeout(completionNavTimerRef.current);
                    navigate(`/patients/${consultation.patientId}`);
                  }}
                >
                  {t("consultation.viewPatientHistory")}
                </Button>
              )}
              {reportExists && (
                <Button
                  variant="outline"
                  onClick={() => {
                    if (completionNavTimerRef.current) clearTimeout(completionNavTimerRef.current);
                    setShowCompletionOverlay(false);
                    setActiveTab("report");
                  }}
                >
                  {t("consultation.viewReport")}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Vitals check dialog */}
      {showVitalsDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">🩺</span>
              <div>
                <h2 className="font-semibold text-lg">{t("consultation.vitalsCheckTitle")}</h2>
                <p className="text-sm text-muted-foreground mt-1">{t("consultation.vitalsCheckDesc")}</p>
              </div>
            </div>
            <div className="flex flex-col gap-2 pt-2">
              <button
                className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                onClick={() => {
                  setShowVitalsDialog(false);
                  if (vitalsField) {
                    patchReportMutation.mutate(
                      vitalsField.isGeneral
                        ? { generalEditedData: { [vitalsField.key]: VITALS_NORMAL_VALUE } }
                        : { editedData: { [vitalsField.key]: VITALS_NORMAL_VALUE } },
                    );
                  }
                }}
              >
                {t("consultation.vitalsCheckConfirmNormal")}
              </button>
              <button
                className="px-4 py-2 text-sm border rounded-md hover:bg-muted transition-colors"
                onClick={() => setShowVitalsDialog(false)}
              >
                {t("consultation.vitalsCheckFillManually")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Completing without a report warning dialog */}
      {showNoReportDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <h2 className="font-semibold text-lg">{t("consultation.noReportTitle")}</h2>
                <p className="text-sm text-muted-foreground mt-1">{t("consultation.noReportDesc")}</p>
              </div>
            </div>
            <div className="flex flex-col gap-2 pt-2">
              <button
                className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                onClick={() => {
                  setShowNoReportDialog(false);
                  handleGenerate();
                }}
              >
                {t("consultation.generateReport")}
              </button>
              <button
                className="px-4 py-2 text-sm border rounded-md hover:bg-muted transition-colors"
                onClick={() => {
                  setShowNoReportDialog(false);
                  completeWithTranscript();
                }}
              >
                {t("consultation.completeWithoutReport")}
              </button>
              <button
                className="px-4 py-2 text-sm text-muted-foreground rounded-md hover:bg-muted transition-colors"
                onClick={() => setShowNoReportDialog(false)}
              >
                {t("common.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Missing blocking fields warning dialog */}
      {missingBlockingFields && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <h2 className="font-semibold text-lg">{t("consultation.missingFieldsTitle")}</h2>
                <p className="text-sm text-muted-foreground mt-1">{t("consultation.missingFieldsDesc")}</p>
              </div>
            </div>
            <ul className="list-disc list-inside space-y-1 text-sm bg-amber-50 border border-amber-200 rounded-md p-3">
              {missingBlockingFields.map((label, i) => (
                <li key={i} className="text-amber-800">{resolveLocalized(label, i18n.language)}</li>
              ))}
            </ul>
            <div className="flex gap-2 justify-end pt-2">
              <button
                className="px-4 py-2 text-sm border rounded-md hover:bg-muted transition-colors"
                onClick={() => setMissingBlockingFields(null)}
              >
                {t("consultation.fixFields")}
              </button>
              <button
                className="px-4 py-2 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors"
                onClick={() => {
                  setMissingBlockingFields(null);
                  doComplete(true);
                }}
              >
                {t("consultation.completeAnyway")}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
